import { useState, useMemo, useRef } from "react";
import { Badge } from "../ui/badge";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import { ShieldAlert, Wine, Pencil, Trash2, ChevronLeft, ChevronRight } from "lucide-react";

const ALLERGEN_ICONS = {
  gluten: "G", leche: "L", huevo: "H", pescado: "P", crustaceos: "Cr",
  moluscos: "Mo", soja: "S", cacahuetes: "Ca", frutos_secos: "FS",
  apio: "Ap", mostaza: "Mo", sesamo: "Se", sulfitos: "Su", altramuces: "Al",
};
const ALLERGEN_COLORS = {
  gluten: "bg-amber-500", leche: "bg-blue-500", huevo: "bg-yellow-500",
  pescado: "bg-sky-600", crustaceos: "bg-red-600", moluscos: "bg-indigo-500",
  soja: "bg-lime-600", cacahuetes: "bg-orange-600", frutos_secos: "bg-amber-700",
  apio: "bg-green-600", mostaza: "bg-yellow-600", sesamo: "bg-stone-500",
  sulfitos: "bg-purple-500", altramuces: "bg-pink-500",
};

function isRecent(dateStr) {
  try {
    const d = new Date(dateStr);
    return Date.now() - d.getTime() < 7 * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

function formatPrice(p) {
  if (!p && p !== 0) return "";
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(p);
}

export default function MenuTPVView({ categories, dishes, getDishesForCategory, onEditDish, onDeleteDish }) {
  const [activeCat, setActiveCat] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const scrollRef = useRef(null);

  const visibleDishes = useMemo(() => {
    if (activeCat === "all") return dishes;
    return getDishesForCategory(activeCat);
  }, [activeCat, dishes, getDishesForCategory]);

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const amount = 340;
    scrollRef.current.scrollBy({ left: dir === "right" ? amount : -amount, behavior: "smooth" });
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-[#0c1929] border-t border-[#1a2d4a]" data-testid="menu-tpv-view">

      {/* Category Tabs */}
      <div className="bg-[#0f2035] border-b border-[#1a2d4a]">
        <ScrollArea className="w-full">
          <div className="flex gap-1 p-2.5 min-w-max">
            <button
              onClick={() => setActiveCat("all")}
              data-testid="tpv-tab-all"
              className={`px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                activeCat === "all"
                  ? "bg-primary text-white shadow-md shadow-primary/30"
                  : "bg-[#162a45] text-[#7ba3cc] hover:bg-[#1c3555] hover:text-white"
              }`}
            >
              Todos ({dishes.length})
            </button>
            {categories.map(cat => {
              const count = getDishesForCategory(cat.category_id).length;
              if (count === 0) return null;
              return (
                <button
                  key={cat.category_id}
                  onClick={() => setActiveCat(cat.category_id)}
                  data-testid={`tpv-tab-${cat.category_id}`}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                    activeCat === cat.category_id
                      ? "bg-primary text-white shadow-md shadow-primary/30"
                      : "bg-[#162a45] text-[#7ba3cc] hover:bg-[#1c3555] hover:text-white"
                  }`}
                >
                  {cat.name} ({count})
                </button>
              );
            })}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>

      {/* Carousel */}
      <div className="relative flex-1 min-h-0 flex items-center px-2">
        {visibleDishes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-[#4a6a8a]">
            <ShieldAlert className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">No hay platos en esta categoria</p>
          </div>
        ) : (
          <>
            {/* Left arrow */}
            <button
              onClick={() => scroll("left")}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#0f2035]/90 border border-[#1a2d4a] flex items-center justify-center text-[#7ba3cc] hover:text-white hover:bg-[#1c3555] transition-colors shadow-lg"
              data-testid="tpv-scroll-left"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Right arrow */}
            <button
              onClick={() => scroll("right")}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#0f2035]/90 border border-[#1a2d4a] flex items-center justify-center text-[#7ba3cc] hover:text-white hover:bg-[#1c3555] transition-colors shadow-lg"
              data-testid="tpv-scroll-right"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Scrollable row */}
            <div
              ref={scrollRef}
              className="flex gap-4 overflow-x-auto px-8 pb-2 scroll-smooth snap-x snap-mandatory"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
              data-testid="tpv-carousel"
            >
              {visibleDishes.map(dish => {
                const isBeverage = dish.source === "universal_beverage" || (dish.tags || []).includes("bebida");
                const isSelected = selectedId === dish.dish_id;
                return (
                  <div
                    key={dish.dish_id}
                    data-testid={`tpv-dish-${dish.dish_id}`}
                    className={`flex-shrink-0 w-[280px] bg-[#0f2035] rounded-2xl border overflow-hidden flex flex-col transition-all duration-300 group snap-start cursor-pointer ${
                      isSelected
                        ? "border-primary shadow-[0_0_24px_rgba(224,93,58,0.35)] scale-[1.03] z-10 ring-2 ring-primary/50"
                        : "border-[#1a2d4a] hover:border-primary/40"
                    }`}
                    onClick={() => { setSelectedId(dish.dish_id); onEditDish?.(dish); }}
                  >
                    {/* Image */}
                    <div className="relative h-52 bg-[#091520] overflow-hidden">
                      {dish.image_url ? (
                        <img
                          src={dish.image_url}
                          alt={dish.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#2a4060]">
                          {isBeverage ? <Wine className="w-14 h-14" /> : (
                            <svg className="w-14 h-14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          )}
                        </div>
                      )}

                      {/* Badges */}
                      <div className="absolute top-2.5 right-2.5 flex gap-1.5">
                        {dish.created_at && isRecent(dish.created_at) && (
                          <Badge className="bg-red-500 text-white text-[10px] px-2 py-0.5 shadow-lg font-bold">New</Badge>
                        )}
                        {isBeverage && (
                          <Badge className="bg-cyan-500/90 text-white text-[10px] px-2 py-0.5 shadow-lg font-bold">Bebida</Badge>
                        )}
                      </div>

                      {/* Allergen icons */}
                      {dish.allergens?.length > 0 && (
                        <div className="absolute bottom-2.5 left-2.5 flex gap-1">
                          {(Array.isArray(dish.allergens) ? dish.allergens : []).slice(0, 6).map(a => (
                            <span
                              key={a}
                              title={a}
                              className={`w-6 h-6 rounded-full ${ALLERGEN_COLORS[a] || "bg-gray-500"} text-white text-[9px] font-bold flex items-center justify-center shadow-lg border border-white/20`}
                            >
                              {ALLERGEN_ICONS[a] || a.charAt(0).toUpperCase()}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Edit overlay on hover */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                          <span className="w-10 h-10 rounded-full bg-card/90 flex items-center justify-center shadow-lg">
                            <Pencil className="w-4 h-4 text-[#0c1929]" />
                          </span>
                          <span
                            className="w-10 h-10 rounded-full bg-red-500/90 flex items-center justify-center shadow-lg"
                            onClick={(e) => { e.stopPropagation(); onDeleteDish?.(dish.dish_id); }}
                          >
                            <Trash2 className="w-4 h-4 text-white" />
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-4 flex-1 flex flex-col">
                      <h4 className="text-white text-sm font-bold leading-tight line-clamp-1">{dish.name}</h4>
                      {dish.description && (
                        <p className="text-[#5a7a9a] text-xs mt-1 line-clamp-2 leading-relaxed">{typeof dish.description === "string" ? dish.description : ""}</p>
                      )}
                      <p className="text-white font-bold text-lg mt-auto pt-2">{formatPrice(dish.price)}</p>
                    </div>

                    {/* Status bar */}
                    <div className={`px-4 py-2.5 text-center text-[10px] font-bold uppercase tracking-wider ${
                      dish.is_active !== false ? "bg-cyan-500/20 text-cyan-400" : "bg-red-500/15 text-red-400"
                    }`}>
                      {dish.is_active !== false ? "Activo" : "Inactivo"}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Footer info */}
      <div className="px-4 pb-3 flex items-center justify-between">
        <p className="text-[#4a6a8a] text-[11px]">{visibleDishes.length} platos {activeCat !== "all" ? "en esta categoria" : "en total"}</p>
        <p className="text-[#3a5a7a] text-[10px]">Click en un plato para editar</p>
      </div>
    </div>
  );
}
